/**
 * RequireSuperuser Route Guard Component
 * Story PPA-2.1: Add Superuser Role and Restrict Prompt Management Access
 *
 * This component restricts access to routes that require superuser privileges.
 * Non-superusers are shown a 403 Forbidden page.
 *
 * Usage:
 *   <RequireSuperuser>
 *     <PromptManagementPage />
 *   </RequireSuperuser>
 */
import { Navigate, useLocation } from 'react-router-dom';
import { loginPathFor } from '../../../lib/utils/loginPath';
import { useAuth } from '../../../lib/contexts/AuthContext';
import { type ReactNode } from 'react';
import { Button } from '@/components/ui/button';

interface RequireSuperuserProps {
  children: ReactNode;
}

export default function RequireSuperuser({ children }: RequireSuperuserProps) {
  const location = useLocation();
  const { isAuthenticated, isLoading, user } = useAuth();

  // Show loading spinner while checking authentication
  if (isLoading) {
    return (
      <div className="min-h-full flex items-center justify-center bg-canvas">
        <div className="text-center">
          <svg
            className="animate-spin motion-reduce:animate-none h-12 w-12 text-ink-muted mx-auto"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            ></circle>
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            ></path>
          </svg>
          <p className="mt-4 text-ink-muted">Loading...</p>
        </div>
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to={loginPathFor(location)} replace />;
  }

  // Show 403 Forbidden page if user is not a superuser.
  //
  // 🔴 `!== true`, not `!user?.is_superuser` (T-20260916-3 review P2). The
  // truthy form disagreed with the NAV, which T-20260916-1 narrowed to
  // `user?.is_superuser === true`, so a payload carrying the STRING 'true' — or
  // 1, or an object — hid every `superuserOnly` row while this guard let the URL
  // through. That is the nav-says-one-thing / route-says-another failure the
  // whole T-20260916-3 change exists to eliminate, running in the less obvious
  // direction (surface reachable, row invisible, nobody notices).
  //
  // It only became reachable when Byte Cost moved here: its old gate,
  // `RequireOwner`, goes through `usePermissions.isOwner()`, which already
  // coerces strictly. Contract, shared with the harness helper
  // `permissions/is-superuser.ts` and with `Sidebar.tsx`: anything other than a
  // literal `true` is not a superuser. `__tests__/App.routes.test.tsx` pins the
  // matrix at this route.
  if (user?.is_superuser !== true) {
    return (
      <div className="min-h-full flex items-center justify-center bg-canvas">
        <div className="text-center max-w-md mx-auto px-4">
          <div className="text-6xl font-bold text-danger mb-4">403</div>
          <h1 className="text-2xl font-semibold text-ink mb-2">
            Access Denied
          </h1>
          <p className="text-ink-muted mb-6">
            This page requires superuser privileges. If you believe you should have access,
            please contact your system administrator.
          </p>
          <Button asChild variant="primary">
            <a href="/">Return to Dashboard</a>
          </Button>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
